import * as React from 'react'
import OrderForm from './components/OrderForm'
import useFetchWithAuth from 'hooks/useFetchWithAuth'
import { ErrorContext } from 'contexts/errorContext'
import { OrdersContext } from 'contexts/ordersContext'
import { useParams } from 'react-router-dom'
import { flattenToNested } from 'utils/dotNotation'
import { money } from 'utils/money'

/**
 * OrderEdit component for displaying order create form
 */
const OrderEdit = () => {
  const initFormData = { 
    customer: { firstName: '', lastName: '', email: '', phone: '' },
    type: '',
    deliveryDate: '',
    discountPct: '',
    deliveryCharges: '',
    isPaid: '',
    deliveryAddress: { address1: '', address2: '', city: '', province: '', zip: '' },
    items: []
  }

  const [formData, setFormData] = React.useState(initFormData)
  const [formErrors, setFormErrors] = React.useState({})
  const { orderId } = useParams()
  const { showError } = React.useContext(ErrorContext)
  const { resetOrders } = React.useContext(OrdersContext)
  const fetchWithAuth = useFetchWithAuth()

  // Fetches order details
  React.useEffect(() => {
    const fetchOrderDetails = async () => {
      try {
        const response = await fetchWithAuth(`/api/orders/${orderId}`)
        const data = await response.json()
        const order = {
          ...data,
          discountPct: data.discount > 0 ? (data.discount / (data.subTotal + data.discount)) * 100 : '',
          discount: parseFloat(money.fromCents(data.discount)),
          deliveryCharges: parseFloat(money.fromCents(data.deliveryCharges))
        }
        setFormData(order)
      } catch (error) {
        showError('Failed to fetch order details. Please try again or refresh the page.')
        console.error(error.message)
      }
    }

    fetchOrderDetails()
  }, [orderId])

  // Form submission
  const handleSubmit = async (data) => {
    setFormErrors({})

    const response = await fetchWithAuth(`/api/orders/${orderId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ 
        ...data, 
        items: data.items.map(({ product, ...item }) => item)
      })
    })

    const json = await response.json()

    if (response.ok) {
      resetOrders()
      return { status: 'success', message: `Order updated successfully!` }
    }

    if ('errors' in json) {
      setFormErrors(flattenToNested(json.errors))
      return { status: 'error', message: 'Submission failed!' }
    }

    return { status: 'error', message: 'Something went wrong. Please try again.' }
  }

  if (!formData._id) return

  return (
    <OrderForm title={`Edit Order ${formData.orderNo}`} formData={formData} setFormData={setFormData} formErrors={formErrors} submitForm={handleSubmit} />
  )
}

export default OrderEdit